import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box, Grid, Alert, Typography, Divider, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import RfqFormStepNav from '../../components/material/RfqFormStepNav';
import RfqLineItemsEditor from '../../components/material/RfqLineItemsEditor';
import RfqVendorSelector from '../../components/material/RfqVendorSelector';
import RfqAttachmentsPanel from '../../components/material/RfqAttachmentsPanel';
import { createRfqWithItemsAndVendors } from '../../api/rfq';
import useItemNames from '../../hooks/useItemNames';
import { useEmrContext } from '../../hooks/useEmrContext';
import { RFQ_TYPES } from '../../constants/rfqManagement';
import { emptyRfqLine, toRfqCreatePayload, validateRfqDates } from '../../utils/rfqEntity';
import { generateTxnNumber } from '../../utils';
import { IPD_COLORS } from '../../theme';

const FORM_ID = 'rfq-form';
const STEPS = 4;

function ReviewRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary, textAlign: 'right' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function RfqForm() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const preselectedVendorId = useMemo(
    () => new URLSearchParams(location.search).get('vendor_id') || '',
    [location.search],
  );
  const emr = useEmrContext();
  const listUrl = url.replace('/rfq/new', '/rfqs');
  const detailBase = url.replace('/rfq/new', '/rfqs');

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [vendorError, setVendorError] = useState(null);
  const [lines, setLines] = useState([{ ...emptyRfqLine() }]);
  const [vendorIds, setVendorIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const submitModeRef = useRef('draft');

  const { getBaseUomCode, resolveItemId } = useItemNames();

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      rfq_number: generateTxnNumber('RFQ'),
      rfq_title: '',
      rfq_type: 'STANDARD',
      issue_date: dayjs().format('YYYY-MM-DD'),
      submission_deadline: dayjs().add(7, 'day').format('YYYY-MM-DD'),
      minimum_required_quotes: 3,
      department_id: emr.department_id || '',
      terms_and_conditions: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (emr.department_id) setValue('department_id', emr.department_id);
  }, [emr.department_id, setValue]);

  const formValues = watch();
  const submissionDeadline = formValues.submission_deadline;
  const typeOptions = RFQ_TYPES.map((t) => ({ value: t.value, label: t.label }));
  const validLineCount = lines.filter((l) => l.item_id && Number(l.required_quantity) > 0).length;
  const typeLabel = typeOptions.find((t) => t.value === formValues.rfq_type)?.label || formValues.rfq_type;

  async function validateStep(step) {
    if (step === 0) {
      const ok = await trigger(['rfq_number', 'rfq_title', 'rfq_type', 'issue_date', 'submission_deadline']);
      if (!ok) return false;
      const dateErr = validateRfqDates(formValues.issue_date, formValues.submission_deadline);
      if (dateErr) {
        setSaveError(dateErr);
        return false;
      }
      setSaveError(null);
      return true;
    }
    if (step === 1) {
      if (validLineCount === 0) {
        setSaveError('Add at least one line item with item and quantity.');
        return false;
      }
      setSaveError(null);
      return true;
    }
    if (step === 2) {
      if (vendorIds.length === 0) {
        setVendorError('Select at least one approved vendor to invite.');
        return false;
      }
      setVendorError(null);
      return true;
    }
    return true;
  }

  async function goNext() {
    const ok = await validateStep(activeStep);
    if (!ok) return;
    setActiveStep((s) => Math.min(s + 1, STEPS - 1));
  }

  function goBack() {
    setSaveError(null);
    setVendorError(null);
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  async function goToStep(index) {
    if (index < activeStep) {
      setActiveStep(index);
      return;
    }
    for (let s = activeStep; s < index; s += 1) {
      const ok = await validateStep(s);
      if (!ok) {
        setActiveStep(s);
        return;
      }
    }
    setActiveStep(index);
  }

  function validateAll() {
    const dateErr = validateRfqDates(formValues.issue_date, formValues.submission_deadline);
    if (dateErr) {
      setSaveError(dateErr);
      setActiveStep(0);
      return false;
    }
    if (validLineCount === 0) {
      setSaveError('Add at least one line item with item and quantity.');
      setActiveStep(1);
      return false;
    }
    if (vendorIds.length === 0) {
      setVendorError('Select at least one approved vendor to invite.');
      setActiveStep(2);
      return false;
    }
    setSaveError(null);
    setVendorError(null);
    return true;
  }

  async function onSubmit(data) {
    if (!validateAll()) return;

    const publish = submitModeRef.current === 'publish';
    submitModeRef.current = 'draft';
    setSaving(true);
    setSaveError(null);

    try {
      const payload = toRfqCreatePayload(data, { vendorIds, attachments });
      const result = await createRfqWithItemsAndVendors(payload, lines, { publish });

      toast.success(publish
        ? 'RFQ published. Selected vendors will be notified.'
        : 'RFQ saved as draft.');

      const rfqId = result?.id || result?.rfq_id;
      history.push(rfqId
        ? `${detailBase}/${rfqId}${publish ? '?published=1' : '?saved=1'}`
        : listUrl);
    } catch (e) {
      setSaveError(e.message || 'Failed to save RFQ');
    } finally {
      setSaving(false);
    }
  }

  function setMode(mode) {
    submitModeRef.current = mode;
  }

  const stepContent = (
    <>
      {activeStep === 0 && (
        <SectionCard title="RFQ Details" subtitle="Basic information and commercial terms">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="rfq_number" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="RFQ Number *" disabled error={errors.rfq_number} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="rfq_type" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvSelect label="RFQ Type *" value={field.value} onChange={field.onChange}
                    options={typeOptions} error={errors.rfq_type} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="minimum_required_quotes" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Minimum Quotes" type="number" inputProps={{ min: 1 }} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="rfq_title" control={control}
                rules={{ required: 'Title is required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="RFQ Title *"
                    placeholder="e.g. Monthly Pharma Supply" error={errors.rfq_title} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="issue_date" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Issue Date *" type="date"
                    InputLabelProps={{ shrink: true }} error={errors.issue_date} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="submission_deadline" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Submission Deadline *" type="date"
                    InputLabelProps={{ shrink: true }} error={errors.submission_deadline} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="department_id" control={control}
                render={({ field }) => (
                  emr.department_id ? (
                    <InvTextField label="Department" value={emr.department_id} disabled />
                  ) : (
                    <InvTextField {...field} label="Department ID" placeholder="Optional UUID" />
                  )
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="terms_and_conditions" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Terms & Conditions" multiline rows={4}
                    placeholder="Payment terms, delivery expectations, warranty…" />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="notes" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Internal Notes" multiline rows={2} />
                )} />
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {activeStep === 1 && (
        <RfqLineItemsEditor
          lines={lines}
          onChange={setLines}
          getBaseUomCode={getBaseUomCode}
          defaultRequiredByDate={submissionDeadline}
        />
      )}

      {activeStep === 2 && (
        <RfqVendorSelector
          selectedIds={vendorIds}
          onChange={setVendorIds}
          preselectedId={preselectedVendorId}
          error={vendorError}
        />
      )}

      {activeStep === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SectionCard title="Review Summary" subtitle="Confirm before saving or publishing">
            <ReviewRow label="RFQ Number" value={formValues.rfq_number} />
            <ReviewRow label="Title" value={formValues.rfq_title} />
            <ReviewRow label="Type" value={typeLabel} />
            <ReviewRow label="Issue Date" value={formValues.issue_date} />
            <ReviewRow label="Submission Deadline" value={formValues.submission_deadline} />
            <Divider sx={{ my: 1, borderColor: IPD_COLORS.border }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
              <Chip label={`${validLineCount} line item${validLineCount !== 1 ? 's' : ''}`} size="small"
                sx={{ fontWeight: 700, backgroundColor: IPD_COLORS.selectedBg, color: IPD_COLORS.primary }} />
              <Chip label={`${vendorIds.length} vendor${vendorIds.length !== 1 ? 's' : ''} invited`} size="small"
                sx={{ fontWeight: 700, backgroundColor: IPD_COLORS.success.bg, color: IPD_COLORS.success.text }} />
              <Chip label={`${attachments.filter((a) => a.file_name).length} attachment(s)`} size="small"
                sx={{ fontWeight: 700 }} />
            </Box>
            {validLineCount > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: IPD_COLORS.textSecondary, mb: 0.5 }}>
                  Items
                </Typography>
                {lines.filter((l) => l.item_id && l.required_quantity).map((l, i) => (
                  <Typography key={i} sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>
                    {resolveItemId(l.item_id)} — Qty {l.required_quantity} {l.uom_code}
                  </Typography>
                ))}
              </Box>
            )}
          </SectionCard>
          <RfqAttachmentsPanel attachments={attachments} onChange={setAttachments} />
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader
        eyebrow="Material Management"
        title="New Request for Quotation"
        description="Follow the steps to create an RFQ, invite vendors, and publish when ready."
        badge={`Step ${activeStep + 1} of ${STEPS}`}
        actions={(
          <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />}
            onClick={() => history.push(listUrl)}>
            Exit
          </InvButton>
        )}
      />

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Box component="form" id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={3} lg={2.75}>
            <RfqFormStepNav
              activeStep={activeStep}
              onStepClick={goToStep}
              completedThrough={activeStep - 1}
            />
          </Grid>

          <Grid item xs={12} md={9} lg={9.25}>
            <Box sx={{ minHeight: 360 }}>
              {stepContent}
            </Box>
          </Grid>
        </Grid>

        {/* Sticky footer navigation */}
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          borderTop: `1px solid ${IPD_COLORS.border}`,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(8px)',
          px: { xs: 2, sm: 3 },
          py: 1.5,
        }}>
          <Box sx={{
            maxWidth: 1400,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}>
            <Box>
              {activeStep > 0 ? (
                <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />}
                  onClick={goBack} disabled={saving}>
                  Previous
                </InvButton>
              ) : (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  Complete each step to publish
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {activeStep < STEPS - 1 ? (
                <InvButton tone="teal" endIcon={<ArrowForwardIcon />}
                  onClick={goNext}>
                  Continue
                </InvButton>
              ) : (
                <>
                  <InvButton type="submit" variant="outlined" tone="teal"
                    loading={saving} onClick={() => setMode('draft')}>
                    Save as Draft
                  </InvButton>
                  <InvButton type="submit" tone="mint"
                    loading={saving} onClick={() => setMode('publish')}>
                    Publish RFQ
                  </InvButton>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
